import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import { useState } from "react";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { RisographBackground } from "../components/RisographBackground";
import { useAuth } from "../hooks/useAuth";

const SCREEN_COUNT = 3;

export function OnboardingIntroPage() {
	const { token, onboardingComplete } = useAuth();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [currentScreen, setCurrentScreen] = useState(0);

	if (token) {
		return (
			<Navigate
				to={onboardingComplete === false ? "/onboarding/prompts" : "/home"}
				replace
			/>
		);
	}

	const screens = [
		{
			headline: t("onboarding.intro.screens.0.headline"),
			body: t("onboarding.intro.screens.0.body"),
			cta: t("onboarding.intro.screens.0.cta"),
		},
		{
			headline: t("onboarding.intro.screens.1.headline"),
			body: t("onboarding.intro.screens.1.body"),
			cta: t("onboarding.intro.screens.1.cta"),
		},
		{
			headline: t("onboarding.intro.screens.2.headline"),
			body: t("onboarding.intro.screens.2.body"),
			cta: t("onboarding.intro.screens.2.cta"),
		},
	] as const;

	const screen = screens[currentScreen];
	const isLast = currentScreen === SCREEN_COUNT - 1;

	function handleCta() {
		if (isLast) {
			navigate("/register");
		} else {
			setCurrentScreen((s) => s + 1);
		}
	}

	return (
		<div className="relative min-h-screen flex flex-col items-center justify-center px-8">
			<RisographBackground />

			{/* Skip link */}
			<button
				type="button"
				onClick={() => navigate("/register")}
				className="absolute top-8 right-6 font-ui text-[11px] uppercase tracking-widest text-ink/50 hover:text-ink border border-ink/20 hover:border-ink/50 rounded-sm px-3 py-1.5 transition-all"
			>
				{t("onboarding.intro.skip")}
			</button>

			{/* Screen content */}
			<div
				key={currentScreen}
				className="max-w-xl w-full text-center animate-fade-in"
			>
				<h1 className="font-display text-5xl md:text-6xl font-bold text-ink leading-tight mb-8">
					{screen.headline}
				</h1>
				<p className="font-body italic text-xl md:text-2xl text-ink/60 leading-relaxed mb-12">
					{screen.body}
				</p>

				{/* Progress dots */}
				<div className="flex justify-center gap-2 mb-10">
					{Array.from({ length: SCREEN_COUNT }).map((_, i) => (
						<span
							key={i}
							className={`w-1.5 h-1.5 rounded-full transition-colors ${
								i === currentScreen ? "bg-ink" : "bg-ink/20"
							}`}
						/>
					))}
				</div>

				<button
					type="button"
					onClick={handleCta}
					className="ink-btn px-10 py-3.5 font-ui text-sm uppercase tracking-widest"
				>
					{screen.cta}
				</button>
			</div>

			{/* Already have account + language switcher */}
			<div className="absolute bottom-8 left-0 right-0 flex flex-col items-center gap-4">
				<div className="text-center">
					<span className="font-ui text-[10px] uppercase tracking-widest text-ink/30">
						{t("onboarding.intro.alreadyHaveAccount")}{" "}
					</span>
					<button
						type="button"
						onClick={() => navigate("/login")}
						className="font-ui text-[10px] uppercase tracking-widest text-ink/50 hover:text-ink/70 transition-colors underline-offset-2 hover:underline"
					>
						{t("onboarding.intro.logIn")}
					</button>
				</div>
				<LanguageSwitcher />
			</div>
		</div>
	);
}
